import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from 'next-themes';
import { readThemeColor } from '../../lib/theme-color.js';

export const MermaidBlock: React.FC<{ children: string }> = ({ children }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [svg, setSvg] = useState<string>('');
  const [error, setError] = useState<string>('');
  const { resolvedTheme } = useTheme();

  useEffect(() => {
    const render = async () => {
      try {
        const mermaid = (await import('mermaid')).default;
        // Re-initialized per render so the diagram follows the active theme. `strict` keeps
        // Mermaid from running click handlers or HTML labels from diagram source (LLM output).
        mermaid.initialize({
          startOnLoad: false,
          securityLevel: 'strict',
          theme: resolvedTheme === 'dark' ? 'dark' : 'default',
          themeVariables: {
            primaryColor: readThemeColor('--color-accent', '#3758f9'),
            primaryTextColor: readThemeColor('--color-heading', '#18181b'),
            primaryBorderColor: readThemeColor('--color-border-strong', '#d4d4d8'),
            lineColor: readThemeColor('--color-muted', '#71717a'),
            secondaryColor: readThemeColor('--color-surface-alt', '#fafafa'),
            tertiaryColor: readThemeColor('--color-surface-hover', '#e4e4e7'),
            background: readThemeColor('--color-surface', '#ffffff'),
            mainBkg: readThemeColor('--color-surface-alt', '#fafafa'),
            nodeBorder: readThemeColor('--color-border-strong', '#d4d4d8'),
            clusterBkg: readThemeColor('--color-surface-alt', '#fafafa'),
            fontSize: '13px',
          },
        });

        const code = typeof children === 'string' ? children.trim() : '';
        if (!code) return;

        const id = `mermaid-${Math.random().toString(36).slice(2, 10)}`;
        const { svg: rendered } = await mermaid.render(id, code);
        setSvg(rendered);
      } catch (err) {
        setError((err as Error).message);
      }
    };

    render();
  }, [children, resolvedTheme]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !svg) return;

    const parsed = new DOMParser().parseFromString(svg, 'image/svg+xml');
    const svgElement = parsed.documentElement;
    if (svgElement.nodeName.toLowerCase() !== 'svg') {
      setError('Mermaid rendered an invalid SVG document');
      return;
    }

    container.replaceChildren(document.importNode(svgElement, true));
    return () => {
      container.replaceChildren();
    };
  }, [svg]);

  if (error) {
    return (
      <pre className="bg-surface-alt p-3 rounded-lg overflow-auto text-xs font-mono my-2 text-danger">
        Diagram error: {error}
      </pre>
    );
  }

  if (!svg) {
    return <div className="bg-surface-alt p-3 rounded-lg my-2 text-muted text-xs">Loading diagram...</div>;
  }

  return <div ref={containerRef} className="my-3 overflow-auto rounded-lg bg-surface-alt p-3" />;
};
